/**
 * Top-layer z-index scale for body-portaled, transient, user-triggered
 * overlays — context menus, the speaker identification modal, tooltips.
 *
 * These values are only applied WHILE a workspace is mounted above the
 * clip-preview modal stack (`CLIP_PREVIEW_Z.translationReview` = 1000030) —
 * i.e. the clip-preview entry of the Translation Review. They are not baked
 * into the overlays directly (that raised every surface app-wide); instead the
 * overlays read the `TOP_LAYER_VARS` custom properties below, and that one
 * workspace sets these values on them while open. Keep every full-surface
 * overlay z BELOW `1000040`. (PLAT-10286)
 *
 * Tooltips sit on top — they describe content inside the modals/menus below.
 */
export const TOP_LAYER_Z = {
  contextMenu: 1000040,
  modalBackdrop: 1000041,
  modal: 1000042,
  tooltip: 1000050,
  /**
   * Above the clip-preview entry's review panel (`CLIP_PREVIEW_Z.translationReview`
   * = 1000030) and below every lifted overlay (1000040+).
   */
  sidebar: 1000031,
} as const;

/**
 * Persistent, corner-docked publish progress drawer (PLAT-10703) — a static
 * resting z, not part of the transient lift above. Sits above the whole
 * clip-preview stack (`CLIP_PREVIEW_Z` tops at 1000030) so publish progress
 * stays visible over modals, and below the `TOP_LAYER_Z` transient overlays
 * (1000040+) so context menus and tooltips still paint over it.
 */
export const PUBLISH_PROGRESS_DRAWER_Z = 1000035;

/**
 * Enlarged hover preview card (`EnlargedPreviewCard`, PLAT-11200) — the
 * floating image/video preview shown beside a media thumbnail on hover.
 *
 * Portaled to `document.body`: it is `position: fixed`, and the clip-preview
 * modal wrapper carries a `backdrop-filter`, which makes that wrapper both a
 * stacking context AND the containing block for fixed descendants. Rendered
 * inline the card therefore lost its own stacking order and was clipped by the
 * settings panel's `overflow: hidden` instead of escaping to the viewport.
 *
 * One step above the top of the clip-preview modal stack
 * (`CLIP_PREVIEW_Z.reelSectionContextMenu` = 1000023) so it paints over the
 * modal and its video player, and below `CLIP_PREVIEW_Z.translationReview`
 * (1000030) and the transient overlays above — a hover preview must never
 * cover a full-surface workspace, a context menu, or a tooltip.
 */
export const ENLARGED_HOVER_PREVIEW_Z = 1000024;

/**
 * CSS custom properties the shared transient overlays read for their z-index,
 * each falling back to that overlay's ORIGINAL resting value when the property
 * is unset. Only a workspace mounted above the clip-preview stack (the
 * Translation Review clip-preview entry, `liftOverlays`) raises these: it sets
 * them to the `TOP_LAYER_Z` values on `document.documentElement` while open and
 * clears them on close. So at rest every surface keeps its historical z, and
 * the lift is scoped to the one case that needs it. Read the matching var in an
 * overlay's style — never hardcode `TOP_LAYER_Z` — so it only lifts when lifted.
 */
export const TOP_LAYER_VARS = {
  contextMenu: '--top-layer-context-menu-z',
  modalBackdrop: '--top-layer-modal-backdrop-z',
  modal: '--top-layer-modal-z',
  tooltip: '--top-layer-tooltip-z',
  /**
   * PLAT-11656 — the app sidebar (`MainMenu`). Lifted only while the clip-preview
   * entry of the Translation Review is open, so the sidebar paints crisp beside
   * the review panel instead of under the clip preview's scrim.
   */
  sidebar: '--top-layer-sidebar-z',
} as const;

/**
 * z-index for a body-portaled overlay opened from INSIDE a shared `Modal`.
 *
 * The two resting values differ: `Modal`'s backdrop falls back to 1000010 while
 * these overlays fall back to 1000006, so a nested one paints BEHIND the dialog
 * that opened it (they portal as siblings of `document.body`, and the lower z
 * loses regardless of mount order). Both read the same var, so a fixed number
 * would break the moment overlays are lifted — one step above whatever the
 * backdrop currently resolves to holds in both states: 1000011 at rest,
 * 1000042 when lifted (`TOP_LAYER_Z.modal`).
 *
 * Only for an overlay a `Modal` opens; an overlay opened from an ordinary panel
 * keeps its resting z.
 */
export const NESTED_IN_MODAL_Z = `calc(var(${TOP_LAYER_VARS.modalBackdrop}, 1000010) + 1)`;

/**
 * PLAT-12049 — a dropdown opened from inside an anchored popover (the Redub
 * Selection popover's Insert Marker list). The popover sits one tier above
 * the clip-preview stack (`CLIP_PREVIEW_Z.translationEditorPopover`, 1000025)
 * at rest and on the context-menu tier (1000040) when lifted, so the list
 * reads the same var and sits two above it: 1000027 at rest (above the shared
 * modal backdrop too, so the whole-dub modal's list is unchanged) and 1000042
 * lifted, level with `NESTED_IN_MODAL_Z` there. Below the tooltip tier.
 */
export const ABOVE_ANCHORED_POPOVER_Z = `calc(var(${TOP_LAYER_VARS.contextMenu}, 1000025) + 2)`;
